/**
 * The engine's top-level operations, each a pure function of a Plan and an explicit `asOf`:
 *
 * - `forecast`  the Dashboard's full forecast: ForecastEngine + MetricsCalculator + GoalTracker
 *               (contract function `ForecastEngine.calculateForecast`).
 * - `metrics`   MetricsCalculator on any cumulative/cashflow series.
 * - `whatIf`    baseline vs. modified forecast, as QuickCalculatorView / ScenarioBuilderView
 *               compose them: same engine, plan with changes applied, differences extracted.
 */
import { addYears, parseDay, startOfMonth, type Day } from './core/calendar.ts';
import { sub, ZERO, type Decimal } from './core/decimal.ts';
import {
  calculateMetrics, currentPosition, DEFAULT_METRICS_INPUTS, type ForecastMetrics, type MetricsInputs, type Position,
} from './calculations/metrics.ts';
import { monthsUntil } from './calculations/dates.ts';
import { calculateForecast, type ForecastOptions, type ForecastResult } from './forecast/forecast-engine.ts';
import { yearlySamples, type YearlySample } from './forecast/payment-bucket.ts';
import type { GoalProgress } from './goals/goal-tracker.ts';
import { GOAL_HORIZON_YEARS, timelineGoals, type GoalLine } from './goals/timeline-goals.ts';
import { activeAssets, balanceDate, cashBaseline, investmentBaseline } from './models/plan.ts';
import type {
  Asset, CalcContext, DepreciatingAsset, Goal, Plan, Property, Series, Transaction,
} from './types/domain.ts';

/** "Today" for the engine: a Day or a "YYYY-MM-DD" string. The calendar is always Gregorian UTC. */
export type AsOf = Day | string;

export const contextAt = (asOf: AsOf): CalcContext => ({ now: typeof asOf === 'string' ? parseDay(asOf) : asOf });

export interface ForecastRequest extends ForecastOptions {
  readonly asOf: AsOf;
}

export interface PlanForecast extends ForecastResult {
  readonly metrics: ForecastMetrics;
  readonly goals: readonly GoalProgress[];
}

const goalLine = (plan: Plan, result: ForecastResult, ctx: CalcContext): GoalLine => ({
  now: result.now,
  today: ctx.now,
  safeWithdrawalRatePercent: plan.configuration.safeWithdrawalRatePercent,
  cumulative: result.cumulative,
});

/** The position at the end of the setup day, from the setup balances alone (decision 7). */
function setupPosition(plan: Plan, ctx: CalcContext): Position {
  const day = balanceDate(plan.configuration, startOfMonth(ctx.now));
  return currentPosition(
    {
      initialBalance: plan.configuration.initialBalance,
      cashSinceDate: day,
      investmentBaseline: null,
      transactions: plan.transactions.filter((t) => t.isActive),
      properties: plan.properties,
      assets: activeAssets(plan),
      depreciatingAssets: plan.depreciatingAssets,
      expectedReturnPercent: plan.configuration.expectedReturnPercent,
    },
    { now: day },
  );
}

/** Every goal read off the line (R26), looking up to 50 years ahead whatever the forecast's timeframe. */
function goalsOn(plan: Plan, result: ForecastResult, ctx: CalcContext, options: ForecastOptions): readonly GoalProgress[] {
  const line = goalLine(plan, result, ctx);
  const extended = (): GoalLine =>
    plan.configuration.timeframeYears >= GOAL_HORIZON_YEARS ? line : goalLine(plan, calculateForecast(withTimeframe(plan, GOAL_HORIZON_YEARS), ctx, options), ctx);
  return timelineGoals(plan.goals, line, extended, () => setupPosition(plan, ctx));
}

/**
 * The Dashboard's forecast: the line from now (R19–R22), its metrics and every goal on it (R26).
 * Metrics read the plan's active transactions at `asOf` with forecastEndDate = asOf + timeframeYears
 * (so D5 applies); their current fields are `now`.
 */
export function forecast(plan: Plan, request: ForecastRequest): PlanForecast {
  const ctx = contextAt(request.asOf);
  const result = calculateForecast(plan, ctx, request);
  const config = plan.configuration;
  const checkpoint = request.checkpoint ?? null;
  const baseline = cashBaseline(plan, checkpoint, result.window.start);
  const metrics = calculateMetrics(
    {
      cumulative: result.cumulative,
      cashflow: result.cashflow,
      initialBalance: baseline.cash,
      transactions: plan.transactions.filter((t) => t.isActive),
      properties: plan.properties,
      assets: activeAssets(plan),
      depreciatingAssets: plan.depreciatingAssets,
      expectedReturnPercent: config.expectedReturnPercent,
      forecastEndDate: addYears(ctx.now, config.timeframeYears),
      cashSinceDate: baseline.since,
      investmentBaseline: investmentBaseline(request.investmentCheckpoint === undefined ? checkpoint : request.investmentCheckpoint),
      current: result.now,
    },
    ctx,
  );
  return { ...result, metrics, goals: goalsOn(plan, result, ctx, request) };
}

/** MetricsCalculator.calculate with Swift's defaults for everything but the cumulative series. */
export const metrics = (
  inputs: Partial<MetricsInputs> & Pick<MetricsInputs, 'cumulative'>,
  { asOf }: { readonly asOf: AsOf },
): ForecastMetrics => calculateMetrics({ ...DEFAULT_METRICS_INPUTS, ...inputs }, contextAt(asOf));

// MARK: What-if

/** Hypothetical additions (appended, as ScenarioBuilderView does) and an optional timeframe override. */
export interface PlanChanges {
  readonly transactions?: readonly Transaction[];
  readonly assets?: readonly Asset[];
  readonly properties?: readonly Property[];
  readonly depreciatingAssets?: readonly DepreciatingAsset[];
  readonly timeframeYears?: number;
}

export const withTimeframe = (plan: Plan, timeframeYears: number): Plan => ({
  ...plan,
  configuration: { ...plan.configuration, timeframeYears },
});

/** The plan with the changes appended; the timeframe applies whether or not anything is added. */
export function applyChanges(plan: Plan, changes: PlanChanges): Plan {
  const extended: Plan = {
    ...plan,
    transactions: [...plan.transactions, ...(changes.transactions ?? [])],
    assets: [...plan.assets, ...(changes.assets ?? [])],
    properties: [...plan.properties, ...(changes.properties ?? [])],
    depreciatingAssets: [...plan.depreciatingAssets, ...(changes.depreciatingAssets ?? [])],
  };
  return changes.timeframeYears === undefined ? extended : withTimeframe(extended, changes.timeframeYears);
}

export interface GoalImpact {
  readonly goal: Goal;
  readonly baselineDate: Day | null;
  readonly modifiedDate: Day | null;
  /** Months sooner under the change (negative = later); null unless both forecasts reach the goal. */
  readonly monthsFaster: number | null;
  /** Reached only under the change. */
  readonly becomesAchievable: boolean;
}

export interface WhatIfResult {
  readonly baseline: ForecastResult;
  readonly modified: ForecastResult;
  /** Final net worth, modified − baseline. */
  readonly netWorthImpact: Decimal;
  readonly yearlyNetWorth: { readonly baseline: readonly YearlySample[]; readonly modified: readonly YearlySample[] };
  readonly goals: readonly GoalImpact[];
}

const lastAmount = (series: Series): Decimal => series[series.length - 1]?.amount ?? ZERO;

/**
 * Baseline vs. modified forecast, both at the changed timeframe and with the same options. Goal
 * dates are each line's (R26: met now is today, else the month's last day, over 50 years); months
 * are measured from `asOf` (DateMath.monthsUntil).
 */
export function whatIf(plan: Plan, changes: PlanChanges, request: ForecastRequest): WhatIfResult {
  const ctx = contextAt(request.asOf);
  const baselinePlan = changes.timeframeYears === undefined ? plan : withTimeframe(plan, changes.timeframeYears);
  const modifiedPlan = applyChanges(plan, changes);
  const baseline = calculateForecast(baselinePlan, ctx, request);
  const modified = calculateForecast(modifiedPlan, ctx, request);
  const years = baselinePlan.configuration.timeframeYears;

  const baselineGoals = goalsOn(baselinePlan, baseline, ctx, request);
  const modifiedGoals = goalsOn(modifiedPlan, modified, ctx, request);
  const monthsAway = (date: Day | null) => (date === null ? null : monthsUntil(date, ctx.now));
  const goals = plan.goals.map((goal, i): GoalImpact => {
    const baselineDate = baselineGoals[i]!.estimatedDate;
    const modifiedDate = modifiedGoals[i]!.estimatedDate;
    const before = monthsAway(baselineDate);
    const after = monthsAway(modifiedDate);
    return {
      goal,
      baselineDate,
      modifiedDate,
      monthsFaster: before !== null && after !== null ? before - after : null,
      becomesAchievable: before === null && after !== null,
    };
  });

  return {
    baseline,
    modified,
    netWorthImpact: sub(lastAmount(modified.cumulative.netWorth), lastAmount(baseline.cumulative.netWorth)),
    yearlyNetWorth: {
      baseline: yearlySamples(baseline.cumulative.netWorth, years),
      modified: yearlySamples(modified.cumulative.netWorth, years),
    },
    goals,
  };
}
